import { useParams, useNavigate } from 'react-router-dom';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(4),
  },
  card: {
    background: '#1e1e2e',
    borderRadius: 16,
    padding: theme.spacing(5),
    maxWidth: 560,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: '#a0a0b0',
    marginBottom: theme.spacing(4),
    fontSize: 15,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    background: '#2a2a3e',
    textAlign: 'left',
  },
  stepName: {
    flex: 1,
    color: '#e0e0f0',
    fontSize: 14,
  },
  successIcon: { color: '#4caf50' },
  errorIcon: { color: '#f44336' },
  pendingIcon: { color: '#666' },
  activeSpinner: { color: '#7c6af7' },
  successBanner: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    borderRadius: 8,
    background: '#1a3a2a',
    border: '1px solid #4caf50',
    color: '#81c784',
    fontSize: 14,
  },
  errorBanner: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    borderRadius: 8,
    background: '#3a1a1a',
    border: '1px solid #f44336',
    color: '#e57373',
    fontSize: 14,
  },
  button: {
    marginTop: theme.spacing(3),
    background: '#7c6af7',
    color: '#fff',
    '&:hover': { background: '#6a58e0' },
  },
}));

function StepIcon({ status }: { status: string }) {
  const classes = useStyles();
  if (status === 'completed') return <CheckCircleIcon className={classes.successIcon} />;
  if (status === 'failed') return <ErrorIcon className={classes.errorIcon} />;
  if (status === 'processing') return <CircularProgress size={20} className={classes.activeSpinner} />;
  return <HourglassEmptyIcon className={classes.pendingIcon} />;
}

export function CustomTaskPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const taskStream = useTaskEventStream(taskId!);
  const classes = useStyles();
  const navigate = useNavigate();

  const steps = taskStream.steps ? Object.values(taskStream.steps) : [];
  const status = taskStream.completed
    ? taskStream.error ? 'failed' : 'completed'
    : 'running';

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        <Typography className={classes.title}>
          {status === 'completed' && '🎉 Done!'}
          {status === 'failed' && '❌ Failed'}
          {status === 'running' && '⚙️ Running...'}
        </Typography>
        <Typography className={classes.subtitle}>
          {status === 'running' ? 'Hang tight — your workflow is in progress.' : ''}
        </Typography>

        {steps.map((step: any) => (
          <Box key={step.id} className={classes.stepRow}>
            <StepIcon status={step.status} />
            <Typography className={classes.stepName}>{step.name}</Typography>
          </Box>
        ))}

        {status === 'completed' && taskStream.output?.text?.length ? (
          taskStream.output.text.map((item: any, i: number) => (
            <Box key={i} className={classes.successBanner}>
              <strong>{item.title}</strong>{item.content ? <><br />{item.content}</> : null}
            </Box>
          ))
        ) : status === 'completed' ? (
          <Box className={classes.successBanner}>✅ All steps completed successfully.</Box>
        ) : null}
        {status === 'failed' && (
          <Box className={classes.errorBanner}>
            ⚠️ {taskStream.error?.message ?? 'An unexpected error occurred.'}
          </Box>
        )}

        <Button className={classes.button} variant="contained" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Box>
    </Box>
  );
}
